import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { loadModels, getFaceDescriptor } from '../services/faceService';
import { User, Camera, Save, Loader2, CheckCircle, AlertTriangle, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, loadUser } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [form, setForm] = useState({ firstName: user?.firstName || '', lastName: user?.lastName || '', phone: user?.phone || '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [savingFace, setSavingFace] = useState(false);
  const [faceStatus, setFaceStatus] = useState('idle');
  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdVisible, setPwdVisible] = useState({ current: false, new: false, confirm: false });
  const [savingPwd, setSavingPwd] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.newPassword.length < 8) { toast.error('Le nouveau mot de passe doit faire au moins 8 caractères'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { toast.error('Les nouveaux mots de passe ne correspondent pas'); return; }
    if (pwd.currentPassword === pwd.newPassword) { toast.error('Le nouveau mot de passe doit être différent de l\'actuel'); return; }
    setSavingPwd(true);
    try {
      await api.post('/users/change-password', { currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      toast.success('Mot de passe changé avec succès');
      setPwd({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du changement');
    } finally { setSavingPwd(false); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put(`/users/${user.id}`, form);
      await loadUser();
      toast.success('Profil mis à jour');
    } catch { toast.error('Erreur'); } finally { setSavingProfile(false); }
  };

  const startCamera = async () => {
    try {
      await loadModels();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(()=>{}); } }, 150);
    } catch { toast.error('Impossible d\'accéder à la caméra'); }
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCameraOpen(false); setFaceStatus('idle');
  };

  const captureAndSaveFace = async () => {
    setSavingFace(true);
    setFaceStatus('loading');
    try {
      if (!videoRef.current || videoRef.current.videoWidth === 0) {
        toast.error('Caméra non prête, attendez quelques secondes');
        setFaceStatus('idle'); setSavingFace(false); return;
      }

      // Charger les modèles et obtenir le descripteur
      await loadModels();
      const result = await getFaceDescriptor(videoRef.current);
      
      const formData = new FormData();
      // Si pas de visage détecté, on envoie un descripteur vide mais on continue
      formData.append('descriptor', JSON.stringify(result ? result.descriptor : Array(128).fill(0)));

      // Capturer la photo
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      
      await new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          try {
            formData.append('photo', blob, 'face.jpg');
            await api.post('/face/register', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setFaceStatus('success');
            toast.success(result ? 'Visage enregistré avec succès !' : 'Photo enregistrée (visage non détecté, réessayez avec plus de lumière)');
            await loadUser();
            setTimeout(stopCamera, 2000);
            resolve();
          } catch(e) { reject(e); }
        }, 'image/jpeg', 0.9);
      });
    } catch (err) {
      console.error('Face error:', err);
      toast.error('Erreur: ' + (err.response?.data?.error || err.message || 'inconnue'));
      setFaceStatus('idle');
    } finally { setSavingFace(false); }
  };

  useEffect(() => () => stopCamera(), []);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Mon Profil</h1>
      <div className="space-y-5">
        {/* Avatar */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center gap-5">
          {user?.avatar
            ? <img src={user.avatar} alt="" className="w-20 h-20 rounded-2xl object-cover" />
            : <div className="w-20 h-20 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
          }
          <div>
            <h2 className="text-xl font-bold text-gray-800">{user?.firstName} {user?.lastName}</h2>
            <p className="text-gray-500">{user?.email}</p>
            <p className="text-blue-600 text-sm mt-1 capitalize">{user?.role} · {user?.company?.name}</p>
          </div>
        </div>

        {/* Infos personnelles */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="font-semibold text-gray-700 mb-4">Informations personnelles</h2>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prénom</label>
                <input value={form.firstName} onChange={e => setForm(p => ({...p, firstName: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom</label>
                <input value={form.lastName} onChange={e => setForm(p => ({...p, lastName: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone</label>
              <input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
            </div>
            <button type="submit" disabled={savingProfile} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
            </button>
          </form>
        </div>

        {/* Reconnaissance faciale */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-700">Reconnaissance faciale</h2>
              <p className="text-gray-400 text-sm mt-0.5">
                {user?.hasFaceRegistered ? '✅ Visage enregistré' : '⚠️ Aucun visage enregistré'}
              </p>
            </div>
            {!cameraOpen ? (
              <button onClick={startCamera} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700">
                <Camera className="w-4 h-4" /> {user?.hasFaceRegistered ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            ) : (
              <button onClick={stopCamera} className="text-gray-500 text-sm hover:text-gray-700">Annuler</button>
            )}
          </div>
          {cameraOpen && (
            <div>
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-4">
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                {faceStatus === 'success' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-green-500/30 backdrop-blur-sm">
                    <CheckCircle className="w-16 h-16 text-white" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-48 h-48 border-2 border-white/50 rounded-full" />
                </div>
              </div>
              <p className="text-sm text-center text-gray-500 mb-4">Centrez votre visage dans le cercle et cliquez sur "Capturer"</p>
              <button onClick={captureAndSaveFace} disabled={savingFace}
                className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2">
                {savingFace ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                Capturer et enregistrer
              </button>
            </div>
          )}
        </div>

        {/* Changer mot de passe */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-700">Changer mon mot de passe</h2>
              <p className="text-gray-400 text-xs mt-0.5">Minimum 8 caractères. Utilise un mot de passe fort.</p>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mot de passe actuel</label>
              <div className="relative">
                <input type={pwdVisible.current ? 'text' : 'password'} required value={pwd.currentPassword}
                  onChange={e => setPwd(p => ({...p, currentPassword: e.target.value}))} autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm" />
                <button type="button" onClick={() => setPwdVisible(p => ({...p, current: !p.current}))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {pwdVisible.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nouveau mot de passe</label>
                <div className="relative">
                  <input type={pwdVisible.new ? 'text' : 'password'} required value={pwd.newPassword}
                    onChange={e => setPwd(p => ({...p, newPassword: e.target.value}))} autoComplete="new-password"
                    className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm" />
                  <button type="button" onClick={() => setPwdVisible(p => ({...p, new: !p.new}))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwdVisible.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirmer le nouveau</label>
                <div className="relative">
                  <input type={pwdVisible.confirm ? 'text' : 'password'} required value={pwd.confirmPassword}
                    onChange={e => setPwd(p => ({...p, confirmPassword: e.target.value}))} autoComplete="new-password"
                    className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm" />
                  <button type="button" onClick={() => setPwdVisible(p => ({...p, confirm: !p.confirm}))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwdVisible.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <button type="submit" disabled={savingPwd}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-sm font-medium">
              {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Changer le mot de passe
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
